/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Search,
  Shield,
  Lock,
  Users,
  Building2,
  ArrowRight,
  CheckCircle2,
  Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import useDocumentTitle from '../hooks/useDocumentTitle';

const PTCPayoutApprovalPage = () => {
  useDocumentTitle('PTC Payout Approval Center');
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState({ total: 0, ready: 0, blocked: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [approving, setApproving] = useState(false);

  const fetchDeals = useCallback(async () => {
    try {
      const response = await api.get('/ptc-finance/payouts', {
        params: { status_filter: statusFilter !== 'all' ? statusFilter : undefined }
      });
      setDeals(response.data.deals || []);
      setSummary(response.data.summary || { total: 0, ready: 0, blocked: 0, approved: 0 });
    } catch (error) {
      console.error('Failed to fetch payout data:', error);
      toast.error('Failed to load payout data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDeals();
  };

  const handleApprovePayout = async () => {
    if (!selectedDeal) return;
    
    setApproving(true);
    try {
      const response = await api.post(`/ptc-finance/deal/${selectedDeal.deal_id}/finalize-payout`);
      toast.success('Payout approved and locked successfully!');
      setShowConfirmDialog(false);
      setSelectedDeal(null);
      fetchDeals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.error === 'PAYOUT_BLOCKED') {
        toast.error(detail.message);
      } else if (typeof detail === 'object' && detail.error === 'ALREADY_APPROVED') {
        toast.error(detail.message);
      } else {
        toast.error(detail?.message || 'Failed to approve payout');
      }
    } finally {
      setApproving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const filteredDeals = deals.filter(deal => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      deal.client_name?.toLowerCase().includes(search) ||
      deal.deal_id?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (deal) => {
    if (deal.payout_status === 'approved') {
      return (
        <Badge className="bg-blue-500 text-white">
          <Lock className="h-3 w-3 mr-1" />
          Approved
        </Badge>
      );
    } else if (deal.payout_status === 'ready') {
      return (
        <Badge className="bg-emerald-500 text-white">
          <CheckCircle className="h-3 w-3 mr-1" />
          GREEN
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500 text-white">
          <XCircle className="h-3 w-3 mr-1" />
          RED
        </Badge>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-emerald-600" />
            PTC Payout Approval Center
          </h1>
          <p className="text-slate-500">Centralized control for all payout approvals - Prestigious Travel Club only</p>
        </div>
        <Button
          variant="outline"
          onClick={handleRefresh}
          disabled={refreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-200 rounded-lg">
                <DollarSign className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Deals</p>
                <p className="text-2xl font-bold text-slate-800">{summary.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 cursor-pointer transition-all ${statusFilter === 'ready' ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-200 bg-emerald-50/50 hover:border-emerald-400'}`}
          onClick={() => setStatusFilter(statusFilter === 'ready' ? 'all' : 'ready')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-200 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Ready (GREEN)</p>
                <p className="text-2xl font-bold text-emerald-700">{summary.ready}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 cursor-pointer transition-all ${statusFilter === 'blocked' ? 'border-red-500 bg-red-50' : 'border-red-200 bg-red-50/50 hover:border-red-400'}`}
          onClick={() => setStatusFilter(statusFilter === 'blocked' ? 'all' : 'blocked')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-200 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Blocked (RED)</p>
                <p className="text-2xl font-bold text-red-700">{summary.blocked}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card 
          className={`border-2 cursor-pointer transition-all ${statusFilter === 'approved' ? 'border-blue-500 bg-blue-50' : 'border-blue-200 bg-blue-50/50 hover:border-blue-400'}`}
          onClick={() => setStatusFilter(statusFilter === 'approved' ? 'all' : 'approved')}
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-200 rounded-lg">
                <Lock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Approved & Locked</p>
                <p className="text-2xl font-bold text-blue-700">{summary.approved}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by client name or deal ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Deals</SelectItem>
            <SelectItem value="ready">Ready for Payout (GREEN)</SelectItem>
            <SelectItem value="blocked">Blocked Deals (RED)</SelectItem>
            <SelectItem value="approved">Approved & Locked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deals List */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Deals Awaiting Approval</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredDeals.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No deals found matching your criteria
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDeals.map((deal) => (
                <div
                  key={deal.deal_id}
                  className={`p-4 rounded-lg border-2 transition-all ${
                    deal.payout_status === 'approved' 
                      ? 'bg-blue-50 border-blue-200' 
                      : deal.payout_status === 'ready'
                        ? 'bg-emerald-50 border-emerald-200 hover:border-emerald-400 cursor-pointer'
                        : 'bg-red-50 border-red-200'
                  }`}
                  onClick={() => deal.payout_status !== 'approved' && setSelectedDeal(deal)}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-slate-800">{deal.client_name}</h3>
                        {getStatusBadge(deal)}
                      </div>
                      <p className="text-xs text-slate-500">
                        Deal ID: {deal.deal_id?.substring(0, 8)}... | {deal.company_name}
                      </p>
                      
                      {/* Financial Summary Row */}
                      <div className="flex flex-wrap gap-4 text-sm mt-2">
                        <div>
                          <span className="text-slate-500">Deal Price:</span>
                          <span className="ml-1 font-medium">{formatCurrency(deal.deal_price)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Collected:</span>
                          <span className="ml-1 font-medium text-emerald-600">{formatCurrency(deal.total_collected)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Remaining:</span>
                          <span className="ml-1 font-medium text-amber-600">{formatCurrency(deal.remaining_balance)}</span>
                        </div>
                        <div>
                          <span className="text-slate-500">Net Profit:</span>
                          <span className={`ml-1 font-medium ${deal.net_profit_actual >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {formatCurrency(deal.net_profit_actual)}
                          </span>
                        </div>
                      </div>

                      {/* Block Reasons */}
                      {deal.payout_status === 'blocked' && deal.block_reasons?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {deal.block_reasons.map((reason, i) => (
                            <Badge key={`${reason}-${i}`} variant="outline" className="text-xs text-red-600 border-red-300">
                              {reason}
                            </Badge>
                          ))}
                        </div>
                      )}

                      {/* Approval Info */}
                      {deal.payout_status === 'approved' && (
                        <div className="mt-2 text-xs text-blue-600">
                          <Lock className="h-3 w-3 inline mr-1" />
                          Approved by {deal.payout_approved_by} on {new Date(deal.payout_approved_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col items-end gap-2">
                      {deal.payout_status === 'ready' && (
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDeal(deal);
                            setShowConfirmDialog(true);
                          }}
                        >
                          <CheckCircle2 className="h-4 w-4 mr-1" />
                          Approve Payout
                        </Button>
                      )}
                      {deal.payout_status === 'blocked' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 border-red-300"
                          disabled
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Blocked
                        </Button>
                      )}
                      {deal.payout_status === 'approved' && (
                        <Badge className="bg-blue-100 text-blue-700 border-blue-300">
                          <Lock className="h-3 w-3 mr-1" />
                          Locked
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-500"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/clients/${deal.deal_id}`);
                        }}
                      >
                        View Deal <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout Breakdown Panel (when deal selected) */}
      {selectedDeal && !showConfirmDialog && (
        <Card className="border-2 border-emerald-300 bg-emerald-50">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-600" />
                Payout Breakdown - {selectedDeal.client_name}
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDeal(null)}
              >
                Close
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="p-3 bg-white rounded-lg border">
                <p className="text-xs text-slate-500 mb-1">Total Collected</p>
                <p className="text-lg font-bold text-emerald-600">{formatCurrency(selectedDeal.total_collected)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-purple-200">
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Users className="h-3 w-3" /> Commission Owed
                </p>
                <p className="text-lg font-bold text-purple-600">{formatCurrency(selectedDeal.commission_owed)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-red-200">
                <p className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Partner Amount Owed
                </p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(selectedDeal.partner_costs_owed)}</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-emerald-200">
                <p className="text-xs text-slate-500 mb-1">PTC Net Profit</p>
                <p className="text-lg font-bold text-emerald-700">{formatCurrency(selectedDeal.net_profit_actual)}</p>
              </div>
            </div>

            {selectedDeal.unallocated > 0 && (
              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg mb-4">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Unallocated Amount: {formatCurrency(selectedDeal.unallocated)}
                </p>
                <p className="text-xs text-amber-600 mt-1">This amount must be allocated before payout can be approved</p>
              </div>
            )}

            {selectedDeal.payout_status === 'ready' && (
              <Button
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={() => setShowConfirmDialog(true)}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Approve Payout
              </Button>
            )}

            {selectedDeal.payout_status === 'blocked' && (
              <div className="text-center py-4">
                <Badge variant="destructive" className="text-sm px-4 py-2">
                  <XCircle className="h-4 w-4 mr-2" />
                  System Status: RED - Cannot Approve
                </Badge>
                <p className="text-xs text-slate-500 mt-2">Resolve all issues before approval</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Shield className="h-5 w-5" />
              Confirm Payout Approval
            </DialogTitle>
            <DialogDescription>
              You are about to approve payout for this deal. This action is PERMANENT and cannot be reversed.
            </DialogDescription>
          </DialogHeader>
          
          {selectedDeal && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedDeal.client_name}</p>
                <p className="text-xs text-slate-500">Deal ID: {selectedDeal.deal_id}</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between p-2 bg-purple-50 rounded border border-purple-200">
                  <span className="text-sm text-purple-700 flex items-center gap-1">
                    <Users className="h-4 w-4" /> Commission Payout
                  </span>
                  <span className="font-bold text-purple-700">{formatCurrency(selectedDeal.commission_owed)}</span>
                </div>
                <div className="flex justify-between p-2 bg-red-50 rounded border border-red-200">
                  <span className="text-sm text-red-700 flex items-center gap-1">
                    <Building2 className="h-4 w-4" /> Partner Payout
                  </span>
                  <span className="font-bold text-red-700">{formatCurrency(selectedDeal.partner_costs_owed)}</span>
                </div>
                <div className="flex justify-between p-2 bg-emerald-50 rounded border border-emerald-200">
                  <span className="text-sm text-emerald-700 flex items-center gap-1">
                    <DollarSign className="h-4 w-4" /> PTC Retained
                  </span>
                  <span className="font-bold text-emerald-700">{formatCurrency(selectedDeal.ptc_amount)}</span>
                </div>
              </div>

              <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                After approval, all financial fields will be LOCKED and no further edits will be allowed.
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={approving}
            >
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleApprovePayout}
              disabled={approving}
            >
              {approving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm Approval
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Safety Rules Info */}
      <Card className="bg-slate-50">
        <CardHeader className="pb-2">
          <CardTitle className="text-base text-slate-700 flex items-center gap-2">
            <Shield className="h-4 w-4" />
            Payout Approval Rules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-white rounded-lg border border-emerald-200">
              <p className="font-medium text-emerald-700 mb-1">GREEN Status Required</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• All payments recorded</li>
                <li>• Commission calculated</li>
                <li>• Partner costs recorded</li>
                <li>• No unallocated funds</li>
                <li>• Ledger balanced</li>
              </ul>
            </div>
            <div className="p-3 bg-white rounded-lg border border-red-200">
              <p className="font-medium text-red-700 mb-1">RED Status = Blocked</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Missing commission</li>
                <li>• Missing partner cost</li>
                <li>• Unbalanced ledger</li>
                <li>• Unallocated funds exist</li>
                <li>• Backend enforced - cannot bypass</li>
              </ul>
            </div>
            <div className="p-3 bg-white rounded-lg border border-blue-200">
              <p className="font-medium text-blue-700 mb-1">After Approval</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li>• Deal marked as "Payout Approved"</li>
                <li>• Financial fields locked</li>
                <li>• No further edits allowed</li>
                <li>• Full audit trail recorded</li>
              </ul>
            </div>
            <div className="p-3 bg-white rounded-lg border">
              <p className="font-medium text-slate-700 mb-1">Scope</p>
              <p className="text-xs text-slate-600">
                This system applies ONLY to:<br />
                • Prestigious Travel Club<br />
                • Prestigious Travel Club Inc
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PTCPayoutApprovalPage;
